// === Admin Portal Theme Context ===
// Theme palette is kept in lockstep with apps/web (see apps/web/src/lib/theme-context.tsx).
// 12 themes total; `default` and `white` are light, the rest dark.
// (Admin doesn't render the web-app nav-bar gradient, so the navBar/navBarEnd
// fields from the web definition are intentionally omitted here.)

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useAuth, getSupabaseClient } from './auth';

export interface ThemeDefinition {
  readonly mode: 'light' | 'dark';
  readonly primary: string;
  readonly secondary: string;
  readonly background: {
    readonly default: string;
    readonly paper: string;
  };
}

export interface ThemeOption {
  readonly id: string;
  readonly name: string;
  readonly bg: string;
  readonly paper: string;
  readonly gradient: string;
}

export interface ThemeContextValue {
  readonly currentTheme: string;
  readonly changeTheme: (themeId: string) => Promise<void>;
  readonly themeOptions: readonly ThemeOption[];
  readonly gradientCards: boolean;
  readonly toggleGradientCards: () => void;
  readonly isThemeLoaded: boolean;
}

const THEME_STORAGE_KEY = 'adminThemeColor';
const GRADIENT_STORAGE_KEY = 'adminGradientCards';

// Coloured themes (plum, violet, red, green, orange, yellow, slate) use near-black
// backgrounds tinted with their hue. Their original mid-tone backgrounds (plum
// #4a3d5c, slate #263238, orange #cc5500...) muted the translucent card and tile
// tints and made the whole app look washed out. The picker swatches in
// themeOptions keep the bright colours so each theme stays recognisable.
const themes: Readonly<Record<string, ThemeDefinition>> = {
  default:  { mode: 'light', primary: '#2e7d32', secondary: '#ff8f00', background: { default: '#f5f5f5', paper: '#ffffff' } },
  charcoal: { mode: 'dark',  primary: '#2e7d32', secondary: '#ff8f00', background: { default: '#121212', paper: '#1e1e1e' } },
  dark:     { mode: 'dark',  primary: '#2e7d32', secondary: '#ff8f00', background: { default: '#000000', paper: '#121212' } },
  plum:     { mode: 'dark',  primary: '#ab47bc', secondary: '#2ecc71', background: { default: '#1a1025', paper: '#2a1b3d' } },
  violet:   { mode: 'dark',  primary: '#7e57c2', secondary: '#2ecc71', background: { default: '#130d24', paper: '#221a3d' } },
  red:      { mode: 'dark',  primary: '#e53935', secondary: '#2ecc71', background: { default: '#1c0b0b', paper: '#311515' } },
  green:    { mode: 'dark',  primary: '#43a047', secondary: '#ffcc02', background: { default: '#0b1a10', paper: '#152c1d' } },
  blue:     { mode: 'dark',  primary: '#90caf9', secondary: '#2e7d32', background: { default: '#0a1929', paper: '#132f4c' } },
  white:    { mode: 'light', primary: '#2e7d32', secondary: '#1565c0', background: { default: '#f5f5f5', paper: '#ffffff' } },
  orange:   { mode: 'dark',  primary: '#ef6c00', secondary: '#2ecc71', background: { default: '#1c1007', paper: '#301d0d' } },
  yellow:   { mode: 'dark',  primary: '#f9a825', secondary: '#2ecc71', background: { default: '#181606', paper: '#2a270c' } },
  slate:    { mode: 'dark',  primary: '#78909c', secondary: '#80cbc4', background: { default: '#0f171b', paper: '#1c272d' } },
} as const;

// Swatches shown in the Settings dialog colour picker — order mirrors apps/web.
export const themeOptions: readonly ThemeOption[] = [
  { id: 'default',  name: 'Default',  bg: '#2e7d32', paper: '#f5f5f5', gradient: 'linear-gradient(135deg, #1b5e20 0%, #388e3c 100%)' },
  { id: 'white',    name: 'White',    bg: '#ffffff', paper: '#f5f5f5', gradient: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)' },
  { id: 'charcoal', name: 'Charcoal', bg: '#121212', paper: '#1e1e1e', gradient: 'linear-gradient(135deg, #000000 0%, #1e1e1e 100%)' },
  { id: 'blue',     name: 'Blue',     bg: '#0a1929', paper: '#132f4c', gradient: 'linear-gradient(135deg, #0a1929 0%, #132f4c 100%)' },
  { id: 'dark',     name: 'Dark',     bg: '#000000', paper: '#121212', gradient: 'linear-gradient(135deg, #000000 0%, #212121 100%)' },
  { id: 'slate',    name: 'Slate',    bg: '#263238', paper: '#37474f', gradient: 'linear-gradient(135deg, #1c313a 0%, #455a64 100%)' },
  { id: 'plum',     name: 'Plum',     bg: '#4a3d5c', paper: '#5c4d73', gradient: 'linear-gradient(135deg, #3d2d5c 0%, #7b5ea0 100%)' },
  { id: 'violet',   name: 'Violet',   bg: '#3d2d5c', paper: '#4d3d73', gradient: 'linear-gradient(135deg, #2d1f4a 0%, #5c35a0 100%)' },
  { id: 'red',      name: 'Red',      bg: '#4a1f1f', paper: '#5c2a2a', gradient: 'linear-gradient(135deg, #3d0f0f 0%, #7b2525 100%)' },
  { id: 'green',    name: 'Green',    bg: '#1f4a2d', paper: '#2a5c3a', gradient: 'linear-gradient(135deg, #1a3d24 0%, #2e7d32 100%)' },
  { id: 'orange',   name: 'Orange',   bg: '#cc5500', paper: '#e67300', gradient: 'linear-gradient(135deg, #bf4000 0%, #e65100 100%)' },
  { id: 'yellow',   name: 'Yellow',   bg: '#857800', paper: '#a89500', gradient: 'linear-gradient(135deg, #5d5300 0%, #c8aa00 100%)' },
] as const;

const THEMES: Readonly<Record<string, ThemeDefinition>> = themes;

const DEFAULT_THEME: ThemeDefinition = themes['default']!;

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function AdminThemeProvider({ children }: { readonly children: ReactNode }) {
  const { user } = useAuth();
  const [currentTheme, setCurrentTheme] = useState<string>(
    () => localStorage.getItem(THEME_STORAGE_KEY) ?? 'default',
  );
  const [gradientCards, setGradientCards] = useState<boolean>(
    () => localStorage.getItem(GRADIENT_STORAGE_KEY) !== 'false',
  );
  const [isThemeLoaded, setIsThemeLoaded] = useState<boolean>(
    () => localStorage.getItem(THEME_STORAGE_KEY) !== null,
  );

  const toggleGradientCards = (): void => {
    setGradientCards((prev) => {
      const next = !prev;
      localStorage.setItem(GRADIENT_STORAGE_KEY, String(next));
      return next;
    });
  };

  // Load theme saved in DB when user signs in
  // DB sync is silent — localStorage value is shown immediately to avoid flash
  useEffect(() => {
    if (!user) {
      setIsThemeLoaded(true); // Guest mode — use localStorage
      return;
    }

    void (async () => {
      try {
        const supabase = await getSupabaseClient();
        const { data, error } = await supabase
          .from('user_preferences')
          .select('theme_color')
          .eq('id', user.id)
          .maybeSingle();

        if (!error && data?.theme_color) {
          setCurrentTheme(data.theme_color as string);
          localStorage.setItem(THEME_STORAGE_KEY, data.theme_color as string);
        }
      } catch {
        // Silently fall back to localStorage value
      }
      finally {
        setIsThemeLoaded(true); // Allow render after DB attempt completes
      }
    })();
  }, [user]);

  const changeTheme = async (themeId: string): Promise<void> => {
    setCurrentTheme(themeId);
    localStorage.setItem(THEME_STORAGE_KEY, themeId);

    try {
      const supabase = await getSupabaseClient();
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        await supabase
          .from('user_preferences')
          .upsert({ id: authUser.id, theme_color: themeId, updated_at: new Date().toISOString() })
          .eq('id', authUser.id);
      }
    } catch {
      // Theme already updated locally — DB sync failure is non-critical
    }
  };

  const themeConfig: ThemeDefinition = THEMES[currentTheme] ?? DEFAULT_THEME;

  const muiTheme = createTheme({
    palette: {
      mode: themeConfig.mode,
      primary:    { main: themeConfig.primary },
      secondary:  { main: themeConfig.secondary },
      background: themeConfig.background,
    },
    typography: {
      fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    },
    shape: { borderRadius: 8 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600 },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ currentTheme, changeTheme, themeOptions, gradientCards, toggleGradientCards, isThemeLoaded }}>
      <MuiThemeProvider theme={muiTheme}>
        <GlobalStyles styles={{
          '*::-webkit-scrollbar': { width: 8, height: 8 },
          '*::-webkit-scrollbar-track': { background: themeConfig.background.default },
          '*::-webkit-scrollbar-thumb': {
            background: themeConfig.background.paper,
            borderRadius: 4,
            border: `2px solid ${themeConfig.background.default}`,
          },
          '*::-webkit-scrollbar-thumb:hover': { background: themeConfig.primary },
        }} />
        {isThemeLoaded && children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeContext must be used within <AdminThemeProvider>');
  return ctx;
}
